// client/pages/BackupSeed.tsx
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";

export default function BackupSeed() {
  const navigate = useNavigate();
  const [mnemonic, setMnemonic] = useState("");
  const [addresses, setAddresses] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any | null>(null);

  // 🔐 Load authenticated user via cookie
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
          } else {
            navigate("/login");
          }
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.error("[BackupSeed] Auth error:", error);
        navigate("/login");
      }
    };
    fetchUser();
  }, [navigate]);

  // Generate mnemonic and addresses on mount (after user is loaded)
  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch("/api/generate-wallet", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to generate wallets");
        setMnemonic(data.mnemonic);
        setAddresses(data.wallets);
      } catch (e) {
        console.error("Seed generation error:", e);
        toast({ title: "ERROR", description: "Unable to generate seed" });
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // Save wallets to backend
  async function saveAndClose() {
    if (!user) {
      toast({ title: "ERROR", description: "User not authenticated" });
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch("/api/generate-wallet", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userId: user.id,
          mnemonic: mnemonic,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save wallets");
      toast({ title: "WALLETS SAVED", description: "All wallets generated successfully" });
      navigate(-1);
    } catch (e: any) {
      toast({ title: "ERROR", description: e.message || "Unable to save wallets" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container py-16 max-w-xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Backup Seed & Create Addresses</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Save this seed phrase securely. It will be used to derive addresses for multiple blockchains.
      </p>

      <div className="mb-4 p-4 bg-card rounded border">
        <pre className="font-mono text-sm whitespace-pre-wrap break-all">
          {mnemonic || (loading ? "Generating..." : "")}
        </pre>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-semibold mb-2">Derived Addresses</h3>
        <ul className="space-y-2">
          <li className="flex justify-between items-start gap-2">
            <span className="capitalize font-medium text-sm min-w-[80px]">Solana</span>
            <span className="font-mono text-xs break-all text-right">{addresses.solana || "..."}</span>
          </li>
          <li className="flex justify-between items-start gap-2">
            <span className="capitalize font-medium text-sm min-w-[80px]">Bitcoin</span>
            <span className="font-mono text-xs break-all text-right">{addresses.bitcoin || "..."}</span>
          </li>
          <li className="flex justify-between items-start gap-2">
            <span className="capitalize font-medium text-sm min-w-[80px]">Ethereum</span>
            <span className="font-mono text-xs break-all text-right">{addresses.ethereum || "..."}</span>
          </li>
          <li className="flex justify-between items-start gap-2">
            <span className="capitalize font-medium text-sm min-w-[80px]">BSC</span>
            <span className="font-mono text-xs break-all text-right">{addresses.bsc || "..."}</span>
          </li>
        </ul>
      </div>

      <div className="flex gap-2">
        <Button onClick={saveAndClose} disabled={loading}>
          {loading ? "Saving..." : "Save Addresses"}
        </Button>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
